import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiArrowDown } from "react-icons/fi";
import MessageBubble from "./MessageBubble";
import ThinkingIndicator from "./ThinkingIndicator";

const STICK_THRESHOLD = 90; // px from the bottom that still counts as "at the bottom"

/**
 * The transcript.
 *
 * Owns its own scroll container so only the conversation moves while the rail,
 * the composer, and the nav stay put. It follows new content while you are at
 * the bottom and stops following the moment you scroll up to re-read
 * something — the behaviour you expect from Claude and ChatGPT, and the thing
 * the old page could not do because the whole document scrolled and the
 * newest answer was pinned to the top.
 */
export default function MessageList({
  messages = [],
  streamingContent = "",
  isThinking = false,
  scrollable = true,
  onRetry,
  onShare,
  emptyState = null,
}) {
  const scrollRef = useRef(null);
  const [pinned, setPinned] = useState(true);

  const scrollToBottom = useCallback((behavior = "auto") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    setPinned(distance < STICK_THRESHOLD);
  }, []);

  // A new turn was added: ease down to it. Sending your own message always
  // pulls you back to the bottom even if you had scrolled up to re-read
  // something, because you just acted — that is the one case where hijacking
  // the scroll position is what the reader expects.
  useEffect(() => {
    if (!scrollable) return;
    const lastRole = messages[messages.length - 1]?.role;
    if (!pinned && lastRole !== "user") return;
    setPinned(true);
    scrollToBottom("smooth");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, isThinking, scrollable]);

  // Tokens are arriving: keep the bottom edge glued without animating on
  // every chunk, which would fight itself and look jittery.
  useEffect(() => {
    if (!scrollable || !pinned || !streamingContent) return;
    scrollToBottom("auto");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamingContent, scrollable]);

  const isEmpty = messages.length === 0 && !isThinking && !streamingContent;

  const body = (
    <div className="mx-auto w-full max-w-3xl space-y-6 px-4 py-6">
      {messages.map((message, index) => {
        const isLastAssistant =
          message.role === "assistant" && index === messages.length - 1;

        return (
          <MessageBubble
            key={message.id}
            message={message}
            onRetry={
              message.role === "assistant" && (isLastAssistant || message.status === "error")
                ? () => onRetry?.(message, index)
                : undefined
            }
            onShare={
              message.role === "assistant" && message.status !== "error"
                ? () => onShare?.(message, index)
                : undefined
            }
          />
        );
      })}

      {streamingContent && (
        <MessageBubble
          message={{ id: "streaming", role: "assistant", content: streamingContent }}
          isStreaming
        />
      )}

      {isThinking && !streamingContent && <ThinkingIndicator />}
    </div>
  );

  if (!scrollable) {
    return (
      <div role="log" aria-label="Conversation">
        {isEmpty ? emptyState : body}
      </div>
    );
  }

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        role="log"
        aria-label="Conversation"
        className="iq-scroll flex-1 overflow-y-auto"
      >
        {isEmpty ? emptyState : body}
      </div>

      {!pinned && !isEmpty && (
        <button
          type="button"
          onClick={() => {
            setPinned(true);
            scrollToBottom("smooth");
          }}
          className="absolute bottom-4 left-1/2 flex h-9 w-9 -translate-x-1/2 items-center justify-center rounded-full border border-iq-line bg-white text-iq-ink-soft shadow-md transition-colors hover:text-iq-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-iq-brand"
          aria-label="Scroll to the latest message"
        >
          <FiArrowDown className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
